import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASE_URL, IS_TUNNELLED_API } from "../utils/constant";
import { getOrCreateDeviceId, getOrCreateBrowserSessionId } from "../utils/common";
import { rollbackSelectedWishlistCount, updateSelectedWishlistCount, updateSelectedWishlistCountIfDeleteApiGotError } from "../pages/company-wishlist/wishlistSlice";
export const getTokenFromLocalStorage = () => {
  try {
    return localStorage.getItem("token");
  } catch (e) {
    return null;
  }
};
// A request with no token can only be answered 401, and RTK Query caches that
// 401 for the life of the page. Every such request is therefore a bug in the
// caller (a missing `skip`), so name it loudly instead of letting it leave
// silently — `api.endpoint` is the endpoint name, and the stack points at the
// component that mounted it.
const withAuthDiagnostics = (baseQuery) => async (args, apiArgs, extraOptions) => {
  const token = apiArgs.getState()?.app?.token || getTokenFromLocalStorage();
  if (!isUsableToken(token) && !PUBLIC_ENDPOINTS.has(apiArgs.endpoint)) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        `[auth] "${apiArgs.endpoint}" is about to call the API with no session`,
        { args, stack: new Error("unauthenticated request").stack }
      );
    }
  }
  return baseQuery(args, apiArgs, extraOptions);
};

// Endpoints that legitimately run before a session exists.
const PUBLIC_ENDPOINTS = new Set(["login", "signup", "getParamMaster"]);

const isUsableToken = (token) =>
  typeof token === "string" && !!token && token !== "undefined" && token !== "null";

export const api = createApi({
  reducerPath: "api",
  baseQuery: withAuthDiagnostics(fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      // Store first, localStorage only as the fallback for the very first
      // request after a reload. Reading localStorage alone made the token
      // invisible to React, so a query issued a moment before login wrote it
      // went out unauthenticated and stayed that way.
      const token = getState()?.app?.token || getTokenFromLocalStorage();
      // An empty (or literal "undefined"/"null") header is worse than none: the
      // API reports it as "Authorization header not found", which reads like a
      // client bug rather than "there is no session yet".
      if (isUsableToken(token)) {
        headers.set("authorization", token);
      }
      headers.set("x-device-id", getOrCreateDeviceId());
      headers.set("x-session-id", getOrCreateBrowserSessionId());
      // Without this an ngrok-tunnelled API answers every request with the
      // interstitial page, which has no CORS headers (see utils/constant.js).
      if (IS_TUNNELLED_API) headers.set("ngrok-skip-browser-warning", "true");

      return headers;
    },
  })),
  tagTypes: [
    "Influencer", "role", "admin", "status", "myCampaign", "wishlist", "wishlistInfluncers",
    "ApiLogs", "RateLimits", "BlockedList", "UserSessions",
  ],
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (data) => ({ url: "/company/login", method: "POST", body: data }),
    }),
    getRoles: builder.query({
      query: (data) => ({
        url: "/roles",
        method: "GET",
        params: data,
      }),
      providesTags: ["role"],
    }),
    signup: builder.mutation({
      query: (data) => ({ url: "/brand/account", method: "POST", body: data }),
    }),
    addRolesAndPermissions: builder.mutation({
      query: (data) => ({
        url: "/roles",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["role"],
    }),
    addAdminUsers: builder.mutation({
      query: (data) => ({
        url: "/admin/addAdmin",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["admin"],
    }),
    getAdminUsers: builder.query({
      query: (params) => ({
        url: "/admin/getAdminUsers",
        method: "GET",
        params,
      }),
      providesTags: ["admin"],
    }),
    getAdminUserById: builder.query({
      query: (id) => ({
        url: `/admin/getAdminUser/${id}`,
        method: "GET",
      }),
      providesTags: ["admin"],
    }),
    getUserManagement: builder.query({
      query: (data) => ({
        url: "/admin/getAllUsers",
        method: "get",
        params: data,
      }),
      providesTags: ["Influencer", "status"],
    }),
    createInfluencer: builder.mutation({
      query: (data) => ({
        url: "/admin/createUser",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Influencer"],
    }),
    getUser: builder.query({
      query: (id) => `/admin/getUser/${id}`,
      providesTags: ["Influencer", "status"],
    }),
    // Both of these are fetched once, at dashboard mount, and their result
    // decides whether the header and the permission-gated navigation render at
    // all. Refetching on mount means a failure from a previous session is never
    // served from cache after a re-login.
    getUserDetails: builder.query({
      query: (id) => `/admin/getUserDetails/${id}`,
      providesTags: ["Influencer", "status"],
      refetchOnMountOrArgChange: true,
    }),
    getRoleById: builder.query({
      query: (id) => `/roles/${id}`,
      providesTags: ["role"],
      refetchOnMountOrArgChange: true,
    }),
    getAdmin: builder.query({
      query: (id) => ({ url: `/admin/getAdminUser/${id}` }),
      providesTags: ["admin"],
    }),
    updateUser: builder.mutation({
      query: (data) => ({
        url: "/admin/updateUser/",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Influencer"],
    }),
    updateAdmin: builder.mutation({
      query: (data) => ({
        url: "/admin/updateAdmin/",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["admin"],
    }),
    updateUserStatus: builder.mutation({
      query: (data) => ({
        url: "/admin/updateUserStatus",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["status"],
    }),
    getParamMaster: builder.query({
      query: () => ({
        url: "/config/parameter-master",
        method: "GET",
      }),
      providesTags: [""],
    }),
    updateRoles: builder.mutation({
      query: (data) => ({
        url: `/role/${data.id}`,
        method: "PATCH",
        body: data.formData,
      }),
      invalidatesTags: ["role"],
    }),
    deleteRole: builder.mutation({
      query: (id) => ({
        url: `/role/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["role"],
    }),
    deleteInfluencer: builder.mutation({
      query: (id) => ({
        url: `/admin/user/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Influencer"],
    }),
    createNewCampaign: builder.mutation({
      query: (data) => ({
        url: "/brand/createCampaign",
        method: "POST",
        body: data,
      }),
    }),
    getRequestedInfluencers: builder.query({
      query: (params) => ({
        url: "/inflyx-credit/requested",
        method: "GET",
        params,
      }),
      providesTags: ["RequestedInfluencers"],
    }),
    updateInflyxCreditStats: builder.mutation({
      query: (body) => ({
        url: "/inflyx-credit/update",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["RequestedInfluencers", "User"],
    }),
    getInflyxScore: builder.query({
      query: (id) => ({
        url: `/inflyx-credit/score/${id}`,
        method: "GET",
      }),
      providesTags: ["InflyxScore"],
    }),
    getAllMyCampaigns: builder.query({
      query: (data) => ({
        url: "/getAllCampaign",
        method: "GET",
        params: data,
      }),
      providesTags: ["myCampaign"],
    }),
    createPushNotification: builder.mutation({
      query: (data) => ({
        url: "/save-notification-token/",
        method: "POST",
        body: data,
      }),
    }),
    getAllBrands: builder.query({
      query: (data) => ({
        url: "/brands",
        method: "GET",
        params: data,
      }),
      providesTags: ["brand"],
    }),
    createWishlist: builder.mutation({
      query: ({ endpoint = "/wishlist/create", method = "POST", body }) => ({
        url: endpoint,
        method: method,
        body: body,
      }),
      invalidatesTags: ["wishlist"],
    }),
    getUserWishlist: builder.query({
      query: (data) => ({
        url: "/wishlist/getList",
        method: "GET",
        params: data,
      }),
      providesTags: ["wishlist"],
    }),
    deleteWishlist: builder.mutation({
      query: (body) => ({
        url: "/wishlist/delete",
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["wishlist"],
    }),
    getWishlistInfluncers: builder.query({
      query: ({ wishlistId, limit, page, ...params }) => ({
        url: `/wishlist/getInfluncers/${wishlistId}?limit=${limit}&offset=${page * limit}`,
        method: "GET",
        params: params,
      }),
      providesTags: ["wishlistInfluncers"],
    }),
    deleteWishlistInfluencers: builder.mutation({
      query: (body) => ({
        url: "wishlist/deleteInfluncers",
        method: "POST",
        body: body,
      }),
      async onQueryStarted(body, { dispatch, queryFulFilled }) {
        dispatch(rollbackSelectedWishlistCount({ count: body.influncersList.length }));
        try {
          await queryFulFilled;
        } catch {
          dispatch(updateSelectedWishlistCountIfDeleteApiGotError({ count: body.influncersList.length }));
        }
      },
      invalidatesTags: ["wishlistInfluncers"],
    }),
    addWishlistInfluencers: builder.mutation({
      query: (body) => ({
        url: "wishlist/addInfluncers",
        method: "POST",
        body: body,
      }),
      async onQueryStarted(body, { dispatch, queryFulFilled }) {
        dispatch(updateSelectedWishlistCount({ count: body.influncersList.length }));
        try {
          await queryFulFilled;
        } catch {
          dispatch(rollbackSelectedWishlistCount({ count: body.influncersList.length }));
        }
      },
      invalidatesTags: ["wishlistInfluncers"],
    }),
    addCampaignInfluencersFromWishlist: builder.mutation({
      query: (body) => ({
        url: "wishlist/campaign/addInfluncers",
        method: "POST",
        body: body,
      }),
      invalidatesTags: [""],
    }),
    deleteInfluncersFromCampaign: builder.mutation({
      query: ({ campaignId, ...body }) => ({
        url: `wishlist/campaign/deleteInfluncers/${campaignId}`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: [""],
    }),
    getDelinkRequests: builder.query({
      query: () => ({
        url: "/admin/delink-requests",
        method: "GET",
      }),
      providesTags: ["delinkRequests"],
    }),
    updateDelinkRequestStatus: builder.mutation({
      query: (body) => ({
        url: "/admin/delink-request-status",
        method: "POST",
        body,
      }),
      invalidatesTags: ["delinkRequests"],
    }),
    getInflyxCreditList: builder.query({
      query: (params) => ({
        url: "/inflyx-credit/list",
        method: "GET",
        params,
      }),
      providesTags: ["InflyxCreditList"],
    }),
    updateInflyxRequestStatus: builder.mutation({
      query: (body) => ({
        url: "/inflyx-credit/status",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["RequestedInfluencers", "InflyxScore"],
    }),
    // NEW: Withdrawal Flow
    requestWithdrawal: builder.mutation({
      query: (body) => ({
        url: "/inflyx-credit/withdraw",
        method: "POST",
        body,
      }),
    }),
    getPendingWithdrawals: builder.query({
      query: (params) => ({
        url: "/inflyx-credit/withdrawals",
        method: "GET",
        params,
      }),
      providesTags: ["PendingWithdrawals"],
    }),
    processWithdrawal: builder.mutation({
      query: (body) => ({
        url: "/inflyx-credit/withdrawal/process",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["PendingWithdrawals", "InflyxCreditList", "InflyxScore"],
    }),
    sendWithdrawalOtp: builder.mutation({
      query: (body) => ({
        url: "/inflyx-credit/withdrawal/send-otp",
        method: "POST",
        body,
      }),
    }),
    // NEW: Influencer Insights
    getInfluencersWithCoins: builder.query({
      query: (params) => ({
        url: "/inflyx-credit/influencers",
        method: "GET",
        params,
      }),
      providesTags: ["InfluencersWithCoins"],
    }),
    getInfluencerTransactionHistory: builder.query({
      query: ({ influencerId, ...params }) => ({
        url: `/inflyx-credit/transactions/${influencerId}`,
        method: "GET",
        params,
      }),
      providesTags: ["TransactionHistory"],
    }),

    // --- Security & Logs (superadmin). All read-only. ---
    getApiLogs: builder.query({
      query: (params) => ({ url: "/admin/api-logs", method: "GET", params }),
      providesTags: ["ApiLogs"],
    }),
    getApiLogStats: builder.query({
      query: (params) => ({ url: "/admin/api-logs/stats", method: "GET", params }),
      providesTags: ["ApiLogs"],
    }),
    getApiLogDetail: builder.query({
      query: (id) => ({ url: `/admin/api-logs/${id}`, method: "GET" }),
      providesTags: ["ApiLogs"],
    }),
    getRateLimits: builder.query({
      query: () => ({ url: "/admin/rate-limits", method: "GET" }),
      providesTags: ["RateLimits"],
    }),
    getBlockedList: builder.query({
      query: () => ({ url: "/admin/rate-limits/blocked", method: "GET" }),
      providesTags: ["BlockedList"],
    }),
    getActiveUserSessions: builder.query({
      query: (params) => ({ url: "/admin/user-sessions", method: "GET", params }),
      providesTags: ["UserSessions"],
    }),
    getUserSessionDetail: builder.query({
      query: (userId) => ({ url: `/admin/user-sessions/${userId}`, method: "GET" }),
      providesTags: ["UserSessions"],
    }),
  }),
});
export const {
  useLoginMutation,
  useSignupMutation,
  useGetRolesQuery,
  useAddRolesAndPermissionsMutation,
  useGetAdminUsersQuery,
  useAddAdminUsersMutation,
  useGetUserManagementMutation,
  useCreateInfluencerMutation,
  useGetUserQuery,
  useUpdateUserMutation,
  useGetAdminQuery,
  useUpdateAdminMutation,
  useUpdateUserStatusMutation,
  useGetRoleByIdQuery,
  useGetParamMasterQuery,
  useGetUserManagementQuery,
  useUpdateRolesMutation,
  useDeleteRoleMutation,
  useDeleteInfluencerMutation,
  useGetAdminUserByIdQuery,
  useCreateNewCampaignMutation,
  useGetAllMyCampaignsQuery,
  useGetAllBrandsQuery,
  useGetUserDetailsQuery,
  useCreateWishlistMutation,
  useGetUserWishlistQuery,
  useDeleteWishlistMutation,
  useGetWishlistInfluncersQuery,
  useDeleteWishlistInfluencersMutation,
  useAddWishlistInfluencersMutation,
  useAddCampaignInfluencersFromWishlistMutation,
  useDeleteInfluncersFromCampaignMutation,
  useGetRequestedInfluencersQuery,
  useUpdateInflyxCreditStatsMutation,
  useGetInflyxScoreQuery,
  useGetDelinkRequestsQuery,
  useUpdateDelinkRequestStatusMutation,
  useGetInflyxCreditListQuery,
  useUpdateInflyxRequestStatusMutation,
  useRequestWithdrawalMutation,
  useGetPendingWithdrawalsQuery,
  useProcessWithdrawalMutation,
  useSendWithdrawalOtpMutation,
  useGetInfluencersWithCoinsQuery,
  useGetInfluencerTransactionHistoryQuery,
  useGetApiLogsQuery,
  useGetApiLogStatsQuery,
  useGetApiLogDetailQuery,
  useGetRateLimitsQuery,
  useGetBlockedListQuery,
  useGetActiveUserSessionsQuery,
  useGetUserSessionDetailQuery
} = api;
